// hls.js custom loader over the WebRTC data-channel tunnel (design spec §8A.3).
//
// hls.js normally fetches m3u8/.ts over http. For the REMOTE path the cloud webapp
// cannot reach the device's LAN http endpoint, so we give hls.js a custom loader that
// instead routes each request through a PbTunnel (request/response over the data
// channel) and hands the bytes back to hls.js, which still does playlist parse + MSE
// + TS→fMP4 transmux + seek.
//
// A `not_found` tunnel response maps to HTTP 404 so hls.js's error path fires — that
// is the recycle-eviction signal the View-2 c5 refresh hangs off (design spec §7.5).
//
// makeDataChannelLoaderClass returns a Loader<LoaderContext> CLASS (hls.js instantiates
// `config.loader` per request) bound to a tunnel + an onRecycle404 callback. Kept out
// of the component so it is import-light (hls.js types only) and unit-testable.

import type {
  Loader,
  LoaderCallbacks,
  LoaderConfiguration,
  LoaderContext,
  LoaderStats,
} from 'hls.js';
import type { PbTunnel } from './webrtcPlayback';

function freshStats(): LoaderStats {
  const t = { start: 0, first: 0, end: 0 };
  return {
    aborted: false,
    loaded: 0,
    retry: 0,
    total: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { ...t },
    parsing: { ...t },
    buffering: { ...t },
  };
}

export interface DataChannelLoaderDeps {
  tunnel: PbTunnel;
  /** Invoked when a segment request comes back not_found (recycle eviction → c5). */
  onRecycle404?: () => void;
}

/**
 * Build a Loader class for hls.js `config.loader`. Each instance handles one request;
 * load() resolves the tunnel response and calls onSuccess (text for m3u8, ArrayBuffer
 * for .ts) or onError ({code:404} on not_found → onRecycle404).
 */
export function makeDataChannelLoaderClass(deps: DataChannelLoaderDeps): {
  new (config: unknown): Loader<LoaderContext>;
} {
  const { tunnel, onRecycle404 } = deps;

  class DataChannelLoader implements Loader<LoaderContext> {
    context: LoaderContext | null = null;
    stats: LoaderStats = freshStats();
    private aborted = false;

    constructor(_config: unknown) {
      /* hls.js passes its HlsConfig; we ignore it (tunnel carries transport). */
    }

    load(context: LoaderContext, _config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>): void {
      this.context = context;
      this.stats = freshStats();
      this.stats.loading.start = performance.now();
      const t0 = performance.now();
      console.log('[pb-tunnel] → request', context.url, 'responseType=', context.responseType);

      tunnel.requestUrl(context.url).then(
        (resp) => {
          const dt = Math.round(performance.now() - t0);
          const size = resp.text != null ? resp.text.length : resp.bytes?.byteLength ?? 0;
          console.log('[pb-tunnel] ← reply', context.url, 'status=', resp.status, 'bytes/chars=', size, 'in', dt + 'ms', resp.text != null ? '\n' + resp.text.slice(0, 400) : '');
          if (this.aborted) return;
          if (resp.status === 'not_found') {
            onRecycle404?.();
            callbacks.onError({ code: 404, text: 'segment evicted (recycle)' }, context, null, this.stats);
            return;
          }
          if (resp.status !== 'ok') {
            callbacks.onError({ code: 502, text: 'tunnel error' }, context, null, this.stats);
            return;
          }
          const isText = context.responseType !== 'arraybuffer';
          const data: string | ArrayBuffer = isText
            ? resp.text ?? (resp.bytes ? new TextDecoder().decode(resp.bytes) : '')
            : toArrayBuffer(resp.bytes ?? new Uint8Array(0));
          const loaded = isText ? (data as string).length : (data as ArrayBuffer).byteLength;
          this.stats.loaded = loaded;
          this.stats.total = loaded;
          this.stats.loading.first = performance.now();
          this.stats.loading.end = performance.now();
          callbacks.onSuccess({ url: context.url, data }, this.stats, context, null);
        },
        (err: unknown) => {
          console.warn('[pb-tunnel] ✗ error', context.url, err instanceof Error ? err.message : err);
          if (this.aborted) return;
          callbacks.onError({ code: 0, text: err instanceof Error ? err.message : 'tunnel failure' }, context, null, this.stats);
        }
      );
    }

    abort(): void {
      this.aborted = true;
      this.stats.aborted = true;
    }

    destroy(): void {
      this.aborted = true;
      this.context = null;
    }
  }

  return DataChannelLoader;
}

/** Copy a Uint8Array view into a standalone ArrayBuffer hls.js can take ownership of. */
function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}
